import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import QRCode from 'react-qr-code';
import OtpInput from 'react-otp-input';
import { Button } from '../../components/ui/button';
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/authContext';
import { getUserDetails } from '../../services/user.service';
import { getAuthenticatorInfo, verifyAuthenticatorOtp, recoverAuthenticator, resetAuthenticator } from '../../services/authentication.service';
import { useToast } from '../../components/ui/use-toast';
import { KeyRound, RefreshCw, ShieldCheck } from 'lucide-react';
import { QRCodeSetupCardSkeleton } from '../ui/card-skeletons';
import AsyncContent from '../ui/AsyncContent';

const otpInputClassName =
    '!w-11 !h-11 rounded-lg border border-slate-200 bg-white text-center text-lg font-semibold text-[#2E2E2E] focus:border-yavrio-green focus:ring-2 focus:ring-yavrio-green/20 outline-none transition-colors';

const StepLabel = ({ number, title, description }) => (
    <div className="flex items-start gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#2E2E2E] text-xs font-semibold text-white">
            {number}
        </span>
        <div>
            <p className="text-sm font-medium text-[#2E2E2E]">{title}</p>
            {description && <p className="text-sm text-slate-500 mt-0.5">{description}</p>}
        </div>
    </div>
);

const QRCodeSetup = () => {
    const [otp, setOtp] = useState('');
    const [view, setView] = useState('setup');
    const [recoveryCode, setRecoveryCode] = useState('');
    const { user, setUser, setAuthenticated } = useAuth();
    const [isTwoFactorEnabled, setIsTwoFactorEnabled] = useState(false);
    const [otpAuthUrl, setOtpAuthUrl] = useState('');
    const [recoveryCodes, setRecoveryCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const { toast } = useToast();
    const fetchedRef = useRef(false);

    const handleVerifyCode = async () => {
        const response = await verifyAuthenticatorOtp(user.token, otp);
        if (response) {
            switch (response.status) {
                case 200:
                    setRecoveryCodes(response.data.data);
                    setOtp('');
                    setView('recoveryCodes');
                    break;
                case 401:
                    toast({
                        title: 'Invalid Code',
                        description: 'The verification code you entered is incorrect. Please try again.',
                        variant: 'destructive',
                    });
                    break;
                default:
                    toast({
                        title: 'Error',
                        description: 'An unexpected error occurred. Please try again.',
                        variant: 'destructive',
                    });
            }
        }
    };

    const handleEnterRecoveryCode = async () => {
        const recoveryCodeInt = parseInt(recoveryCode, 10);
        const response = await recoverAuthenticator(user.token, recoveryCodeInt);
        if (response) {
            switch (response.status) {
                case 200:
                    const { otpAuthUrl } = response.data.data;
                    setOtpAuthUrl(otpAuthUrl);
                    setRecoveryCode('');
                    setView('setup');
                    break;
                case 401:
                    toast({
                        title: 'Invalid Recovery Code',
                        description: 'The recovery code you entered is incorrect. Please try again.',
                        variant: 'destructive',
                    });
                    break;
                default:
                    toast({
                        title: 'Error',
                        description: 'An unexpected error occurred. Please try again.',
                        variant: 'destructive',
                    });
            }
        }
    };

    const handleResetAuthenticator = async () => {
        const response = await resetAuthenticator(user.token);
        if (response) {
            switch (response.status) {
                case 200:
                    const { otpAuthUrl } = response.data.data;
                    setOtpAuthUrl(otpAuthUrl);
                    setOtp('');
                    setView('setup');
                    toast({
                        title: 'Success',
                        description: 'Authenticator has been reset successfully. Please scan the new QR code.',
                        variant: 'success',
                    });

                    setUser({
                        email: '',
                        role: '',
                        isActive: false,
                        twoFactorEnabled: false,
                    });
                    setAuthenticated(false);
                    break;
                default:
                    toast({
                        title: 'Error',
                        description: 'An unexpected error occurred while resetting the authenticator.',
                        variant: 'destructive',
                    });
            }
        }
    };

    useEffect(() => {
        if (view === 'setup' && !fetchedRef.current) {
            fetchedRef.current = true;
            setLoading(true);

            const fetchUserDetails = async () => {
                const response = await getUserDetails(user.token, user.organisationId, user.id);
                if (response && response.status === 200) {
                    const { twoFactorEnabled } = response.data.data;
                    if (user.twoFactorEnabled !== twoFactorEnabled) {
                        setUser((prevUser) => ({
                            ...prevUser,
                            twoFactorEnabled,
                        }));
                    }
                    setIsTwoFactorEnabled(twoFactorEnabled);
                }
            };

            const fetchAuthenticatorInfo = async () => {
                const response = await getAuthenticatorInfo(user.token);
                if (response && response.status === 200) {
                    const { otpAuthUrl } = response.data.data;
                    setOtpAuthUrl(otpAuthUrl);
                }
            };

            Promise.all([fetchUserDetails(), fetchAuthenticatorInfo()]).finally(() => {
                setLoading(false);
            });
        }

        if (view !== 'setup') {
            fetchedRef.current = false;
            setLoading(false);
        }
    }, [view, user.token, user.organisationId, user.id, user.twoFactorEnabled, setUser]);

    return (
        <AsyncContent
            loading={loading && view === 'setup'}
            skeleton={<QRCodeSetupCardSkeleton />}
        >
        <Card className="overflow-hidden">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-yavrio-green/10 border border-yavrio-green/20">
                            <ShieldCheck className="h-5 w-5 text-yavrio-green" />
                        </span>
                        <div>
                            <CardTitle className="text-lg">Two-factor authentication</CardTitle>
                            <CardDescription>
                                Setup an authenticator app on your phone to approve payments.
                            </CardDescription>
                        </div>
                    </div>
                    {view === 'setup' && (
                        <Badge variant={isTwoFactorEnabled ? 'success' : 'warning'} className="shrink-0">
                            {isTwoFactorEnabled ? 'Active' : 'Not configured'}
                        </Badge>
                    )}
                </div>
            </CardHeader>

            <CardContent className="pt-6">
                {view === 'initial' && (
                    <div className="flex flex-col items-center py-8 text-center">
                        <p className="text-sm text-slate-500 max-w-sm mb-6">
                            You will require a verification code from your authenticator app when approving payments.
                        </p>
                        <Button className="bg-yavrio-green hover:bg-yavrio-green-dark" onClick={() => setView('setup')}>
                            Set up authenticator
                        </Button>
                    </div>
                )}

                {view === 'setup' && (
                    <>
                        {isTwoFactorEnabled ? (
                            <div className="space-y-6">
                                <div className="rounded-lg border border-yavrio-green/20 bg-yavrio-green/5 p-4">
                                    <p className="text-sm font-medium text-yavrio-green">Two-factor authentication is active</p>
                                    <p className="text-sm text-slate-600 mt-1">
                                        Use a recovery code if you have lost access to your authenticator app.
                                    </p>
                                </div>
                                <Button
                                    variant="outline"
                                    className="gap-2 text-slate-700 border-slate-200 hover:bg-slate-50"
                                    onClick={() => setView('recover')}
                                >
                                    <KeyRound className="h-4 w-4" />
                                    Recover authenticator
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-8">
                                <StepLabel
                                    number={1}
                                    title="Scan the QR code"
                                    description="Open your authenticator app and scan the code below."
                                />
                                <div className="flex justify-center">
                                    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                                        <QRCode value={otpAuthUrl} size={200} />
                                    </div>
                                </div>

                                <StepLabel
                                    number={2}
                                    title="Enter verification code"
                                    description="Type the 6-digit code shown in your authenticator app."
                                />
                                <div className="flex justify-center">
                                    <OtpInput
                                        value={otp}
                                        onChange={setOtp}
                                        numInputs={6}
                                        renderSeparator={<span className="text-slate-300 mx-1">-</span>}
                                        renderInput={(props) => <input {...props} className={otpInputClassName} />}
                                    />
                                </div>

                                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                                    <Button
                                        className="bg-yavrio-green hover:bg-yavrio-green-dark min-w-[160px]"
                                        onClick={handleVerifyCode}
                                    >
                                        Verify code
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="gap-2 text-slate-600 border-slate-200 hover:bg-slate-50 min-w-[160px]"
                                        onClick={handleResetAuthenticator}
                                    >
                                        <RefreshCw className="h-4 w-4" />
                                        Refresh QR code
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {view === 'recover' && (
                    <div className="space-y-6 max-w-md mx-auto">
                        <div className="text-center">
                            <p className="text-sm font-medium text-[#2E2E2E]">Enter your recovery code</p>
                            <p className="text-sm text-slate-500 mt-1">
                                Use one of the 8-digit codes you saved when setting up two-factor authentication.
                            </p>
                        </div>
                        <div className="flex justify-center">
                            <OtpInput
                                value={recoveryCode}
                                onChange={setRecoveryCode}
                                numInputs={8}
                                renderInput={(props) => <input {...props} className={otpInputClassName} />}
                            />
                        </div>
                        <div className="flex justify-center gap-3">
                            <Button variant="outline" className="border-slate-200" onClick={() => setView('setup')}>
                                Back
                            </Button>
                            <Button className="bg-yavrio-green hover:bg-yavrio-green-dark" onClick={handleEnterRecoveryCode}>
                                Submit code
                            </Button>
                        </div>
                    </div>
                )}

                {view === 'recoveryCodes' && (
                    <div className="space-y-6">
                        <div>
                            <p className="text-sm font-medium text-[#2E2E2E]">Save your recovery codes</p>
                            <p className="text-sm text-slate-500 mt-1">
                                Store these codes somewhere safe. You will need them to regain access if you lose your authenticator device.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {recoveryCodes.map((code, index) => (
                                <div
                                    key={index}
                                    className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50/50 px-4 py-3"
                                >
                                    <span className="font-mono text-sm font-medium text-[#2E2E2E]">{code.code}</span>
                                    <Badge variant={code.used ? 'muted' : 'success'}>
                                        {code.used ? 'Used' : 'Unused'}
                                    </Badge>
                                </div>
                            ))}
                        </div>
                        <Button className="bg-yavrio-green hover:bg-yavrio-green-dark" onClick={() => setView('setup')}>
                            Continue
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
        </AsyncContent>
    );
};

export default QRCodeSetup;
